import {
  getProductsFromSalesOrder,
  postCreatePurchaseOrderFromSalesOrder,
} from "@/apiv2/purchaseProducts";
import { searchSuppliers } from "@/apiv2/suppliers";
import { createAction, createSetterActions } from "@/redux/helpers/action";
import { handleError } from "@/redux/utils/error";
import { STATUS } from "@/utils/constants";

import { MODULE } from "./constants";
import * as selectors from "./purchase-products-modal.selectors";

export const [
  setLists,
  setSuppliersList,
  setIsFetchingSuppliersStatus,
  setIsFetchingPurchaseProductsStatus,
  setIsCreating,
  setIsGroupedProducts,
  setHasFormSubmitted,
  setSelectedSupplierForAll,
  setIsCreatePurchaseOrderModalOpen,
  setIsCreatingPurchaseOrderModalOpen,
  setIsCreatedPurchaseOrderModalOpen,
  setSelectedProductUids,
  setCreatedPoUids,
] = createSetterActions(MODULE, [
  "lists",
  "suppliersList",
  "isFetchingSuppliersStatus",
  "isFetchingPurchaseProductsStatus",
  "isCreating",
  "isGroupedProducts",
  "hasFormSubmitted",
  "selectedSupplierForAll",
  "isCreatePurchaseOrderModalOpen",
  "isCreatingPurchaseOrderModalOpen",
  "isCreatedPurchaseOrderModalOpen",
  "selectedProductUids",
  "createdPoUids",
]);

export const reset = createAction(`${MODULE}/RESET`);

export const selectProductUid =
  ({ uid, checked } = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const selectedProductUids = selectors.getSelectedProductUids(state);
    dispatch(
      setSelectedProductUids(
        checked
          ? [...selectedProductUids, uid]
          : selectedProductUids.filter((selected) => selected !== uid)
      )
    );
  };

export const updateQuantity =
  ({ uid, quantity }) =>
  (dispatch, getState) => {
    const state = getState();
    const lists = selectors.getLists(state);

    dispatch(
      setLists(
        lists.map((list) => {
          if (list.uid === uid) {
            return { ...list, quantity };
          }
          return list;
        })
      )
    );
  };
export const updateSupplier =
  ({ uid, supplierForCreatedPo } = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const lists = selectors.getLists(state);

    dispatch(
      setLists(
        lists.map((list) => {
          if (list.uid === uid) {
            return { ...list, supplierForCreatedPo };
          }
          return list;
        })
      )
    );

    if (!selectors.getSelectedProductUids(state).includes(uid)) {
      dispatch(selectProductUid({ uid, checked: true }));
    }
  };

export const updateSupplierForEachProducts =
  ({ productList } = {}) =>
  (dispatch, getState) => {
    if (!Array.isArray(productList) || productList.length === 0) {
      return;
    }

    const state = getState();
    const lists = selectors.getLists(state);

    const selectedProductUids = [];
    const formattedLists = lists.map((list) => {
      const supplierUidIfExists = productList.find((product) => product.productUid === list.uid);
      if (supplierUidIfExists) {
        selectedProductUids.push(supplierUidIfExists.productUid);
        const supplierForCreatedPo = supplierUidIfExists.supplierUid;
        return { ...list, supplierForCreatedPo };
      }
      return list;
    });

    dispatch(setLists(formattedLists));
    dispatch(setSelectedProductUids(selectedProductUids));
  };

export const selectAllProducts = (status) => (dispatch, getState) => {
  const state = getState();

  if (status) {
    const uids = selectors.getLists(state).map((product) => product.uid);

    dispatch(setSelectedProductUids(uids));
  } else {
    dispatch(setSelectedProductUids([]));
  }
};

export const fetchPurchaseProducts =
  ({ customerUid, orderUid }) =>
  async (dispatch) => {
    try {
      dispatch(setIsFetchingPurchaseProductsStatus(STATUS.PENDING));

      const { data } = await getProductsFromSalesOrder({ customerUid, orderUid })();
      dispatch(setLists(data));
      dispatch(setIsFetchingPurchaseProductsStatus(STATUS.FULFILLED));
    } catch (err) {
      dispatch(setIsFetchingPurchaseProductsStatus(STATUS.REJECTED));
      handleError(err, dispatch);
    }
  };

export const createPurchaseOrder =
  ({ customerUid, orderUid }) =>
  async (dispatch, getState) => {
    const state = getState();

    const selectedProductUids = selectors.getSelectedProductUids(state);
    const products = selectors
      .getLists(state)
      .filter((p) => selectedProductUids.includes(p.uid) && p.supplierForCreatedPo !== null);

    if (products.length === 0) {
      return;
    }

    const payload = {
      is_grouped_products: selectors.isGroupedProducts(state),
      products: products.map((p) => ({
        quantity: +p.quantity,
        item_uid: p.uid,
        supplier_uid: p.supplierForCreatedPo,
      })),
    };

    try {
      dispatch(setIsCreating(true));
      const { data } = await postCreatePurchaseOrderFromSalesOrder({
        customerUid,
        orderUid,
        payload,
      })();

      dispatch(setCreatedPoUids(data.map((r) => r.uid)));
      dispatch(setIsCreatingPurchaseOrderModalOpen(false));
      dispatch(setIsCreatedPurchaseOrderModalOpen(true));
    } catch (err) {
      handleError(err, dispatch);
    } finally {
      dispatch(setIsCreating(false));
    }
  };

export const fetchAllSuppliers =
  ({ search = "", includeArchived = false }) =>
  async (dispatch) => {
    try {
      dispatch(setIsFetchingSuppliersStatus(STATUS.PENDING));

      const { data } = await searchSuppliers({ search, includeArchived })();
      dispatch(setSuppliersList(data));

      dispatch(setIsFetchingSuppliersStatus(STATUS.FULFILLED));
    } catch (err) {
      dispatch(setIsFetchingSuppliersStatus(STATUS.REJECTED));
      handleError(err, dispatch);
    }
  };
